import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { usePageFocus } from "./usePageFocus";

type DocumentCategory = "contract" | "invoice" | "permit" | "receipt" | "warranty" | "other";

const SIGNED_URL_TTL_SECONDS = 60 * 60;
const BUCKET = "documents";

interface Document {
  id: string;
  file_name: string;
  // Storage path: <projectId>/<uuid>-<filename>. Not a URL.
  file_url: string;
  // Decorated at fetch time. Expires after SIGNED_URL_TTL_SECONDS.
  signed_url: string | null;
  category: DocumentCategory;
  upload_date: string;
  uploaded_by: string;
}

interface DocumentRow {
  id: string;
  file_name: string;
  file_url: string;
  category: DocumentCategory;
  upload_date: string;
  uploaded_by: string;
}

const decorateWithSignedUrls = async (rows: DocumentRow[]): Promise<Document[]> => {
  if (rows.length === 0) return [];

  const paths = rows.map((r) => r.file_url);
  const { data: signed, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);

  if (error) {
    return rows.map((r) => ({ ...r, signed_url: null }));
  }

  return rows.map((r, i) => ({ ...r, signed_url: signed?.[i]?.signedUrl ?? null }));
};

export const useDocuments = (projectId: string) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDocuments = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("documents")
        .select("*")
        .eq("project_id", projectId)
        .order("upload_date", { ascending: false });

      if (fetchError) throw fetchError;

      const decorated = await decorateWithSignedUrls((data ?? []) as DocumentRow[]);
      setDocuments(decorated);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load documents";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const uploadDocument = useCallback(
    async (file: File, category: DocumentCategory, uploadedByUserId: string) => {
      if (!projectId) throw new Error("Project ID is required");

      const objectPath = `${projectId}/${crypto.randomUUID()}-${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(objectPath, file, {
          contentType: file.type || undefined,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data, error: insertError } = await supabase
        .from("documents")
        .insert([
          {
            project_id: projectId,
            uploaded_by: uploadedByUserId,
            file_name: file.name,
            file_url: objectPath,
            category,
            upload_date: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (insertError) {
        await supabase.storage.from(BUCKET).remove([objectPath]).catch(() => {});
        throw insertError;
      }

      const [decorated] = await decorateWithSignedUrls([data as DocumentRow]);
      setDocuments((prev) => [decorated, ...prev]);
    },
    [projectId]
  );

  const deleteDocument = useCallback(
    async (documentId: string) => {
      const target = documents.find((d) => d.id === documentId);

      const { error: deleteError } = await supabase
        .from("documents")
        .delete()
        .eq("id", documentId);

      if (deleteError) throw deleteError;

      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));

      if (target?.file_url) {
        await supabase.storage.from(BUCKET).remove([target.file_url]).catch(() => {});
      }
    },
    [documents]
  );

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  usePageFocus(loadDocuments);

  return {
    documents,
    loading,
    error,
    uploadDocument,
    deleteDocument,
    reloadDocuments: loadDocuments,
  };
};
